// src/lib/constants/appFactory.ts

// ── Build statuses (factory app_build2 machine) ──
export const FACTORY_BUILD_STATUSES = [
  'queued',
  'building',
  'built',
  'preflight_passed',
  'awaiting_customer',
  'failed',
  'superseded',
] as const;
export type FactoryBuildStatus = typeof FACTORY_BUILD_STATUSES[number];

/**
 * A build the farm still owns: queued, leased, or built but not yet checked.
 * `preflight_passed` / `awaiting_customer` are RESTING rows (a new request for
 * the same release supersedes them — a brand fix is real work again);
 * `failed` / `superseded` are terminal.
 */
export const FACTORY_ACTIVE_BUILD_STATUSES = ['queued', 'building', 'built'] as const;

/** A build resting at its checks: built, verified, waiting for a submission or the holder. */
export const FACTORY_RESTING_BUILD_STATUSES = ['preflight_passed', 'awaiting_customer'] as const;

/**
 * A submission a store holds or serves, or the holder still has to hand it —
 * or (3.34.4) one the factory is still preparing: the build behind it is the
 * submission's from the moment the pre-flight opened it, so the Build card does
 * not offer the same release again (the farm would answer the same row — or,
 * once live, only a changed set of inputs is real work: appfactory ADR-0033).
 */
export const FACTORY_STORE_BOUND_SUBMISSION_STATES = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'live',
] as const;

// ── Submission states (factory app_submission machine) ──
export const FACTORY_SUBMISSION_STATES = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'rejected',
  'live',
  'superseded',
] as const;
export type FactorySubmissionState = typeof FACTORY_SUBMISSION_STATES[number];

// ── Display labels ──
export const FACTORY_BUILD_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  building: 'Building',
  built: 'Built',
  preflight_passed: 'Checks Passed',
  awaiting_customer: 'Ready for store',
  failed: 'Failed',
  superseded: 'Superseded',
};

export const FACTORY_SUBMISSION_STATE_LABELS: Record<string, string> = {
  preparing: 'Preparing',
  awaiting_customer: 'Ready to submit',
  submitting: 'Submitting',
  in_review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  live: 'Live',
  superseded: 'Superseded',
};

// ── Badge colors (Mantine palette names) ──
export const FACTORY_BUILD_STATUS_COLORS: Record<string, string> = {
  queued: 'gray',
  building: 'blue',
  built: 'cyan',
  preflight_passed: 'teal',
  awaiting_customer: 'orange',
  failed: 'red',
  superseded: 'gray',
};

export const FACTORY_SUBMISSION_STATE_COLORS: Record<string, string> = {
  preparing: 'gray',
  awaiting_customer: 'orange',
  submitting: 'blue',
  in_review: 'indigo',
  approved: 'teal',
  rejected: 'red',
  live: 'green',
  superseded: 'gray',
};

/**
 * One line of state-specific guidance under a submission's badge — what the
 * state MEANS for the person reading the card (B1: the generic "waiting on
 * your action" badge told nobody what to do).
 */
export const FACTORY_SUBMISSION_STATE_GUIDANCE: Record<string, string> = {
  preparing: 'The build is being checked and packaged. Nothing to do yet.',
  awaiting_customer: 'The package and guide are ready for the store portal.',
  submitting: 'The package is being entered in the store portal.',
  in_review: 'The store is reviewing the channel. Reviews usually take a few business days.',
  approved: 'The store approved the channel. It goes live on the scheduled publish date.',
  rejected: 'The store rejected this submission. A new build starts a fresh submission.',
  live: 'This version is live in the store.',
  superseded: 'A newer submission replaced this one.',
};

/**
 * Store-specific wording over the guidance above (which speaks Roku's "channel"
 * and "store portal"); a state a store does not list falls back to it.
 */
export const FACTORY_STORE_STATE_GUIDANCE: Partial<Record<string, Record<string, string>>> = {
  amazon: {
    preparing: 'The update is being built and checked. Nothing to do yet.',
    awaiting_customer: 'The update is built, checked and ready for the Amazon Appstore.',
    submitting: 'The update is being submitted to the Amazon Appstore.',
    in_review: 'Amazon is reviewing the update. Reviews usually take a few days.',
    approved: 'Amazon approved the update. It reaches devices shortly after approval.',
    rejected: 'Amazon rejected this update. A new build starts a fresh submission.',
  },
  google: {
    preparing: 'The update is being built and checked. Nothing to do yet.',
    awaiting_customer: 'The update is built, checked and ready for Google Play.',
    submitting: 'The update is being published to Google Play.',
    in_review: 'Google Play is reviewing the update. Reviews usually take a few days.',
    approved: 'Google Play approved the update. It reaches devices shortly after.',
    rejected: 'Google Play rejected this update. A new build starts a fresh submission.',
  },
};

// ── Store accounts (4.2 delegated store accounts) ──
export const FACTORY_STORE_ACCOUNT_STATES = [
  'pending',
  'invited',
  'active',
  'action_needed',
  'revoked',
] as const;
export type FactoryStoreAccountState = typeof FACTORY_STORE_ACCOUNT_STATES[number];

export const FACTORY_STORE_ACCOUNT_STATE_LABELS: Record<string, string> = {
  pending: 'Not set up',
  invited: 'Invite sent',
  active: 'Active',
  action_needed: 'Action needed',
  revoked: 'Revoked',
};

export const FACTORY_STORE_ACCOUNT_STATE_COLORS: Record<string, string> = {
  pending: 'gray',
  invited: 'blue',
  active: 'green',
  action_needed: 'orange',
  revoked: 'red',
};

export const FACTORY_STORE_ACCOUNT_HOLDER_KINDS = ['customer', 'reseller', 'streamhoster'] as const;
export type FactoryStoreAccountHolderKind = typeof FACTORY_STORE_ACCOUNT_HOLDER_KINDS[number];

export const FACTORY_STORE_ACCOUNT_SUBMITTERS = ['operator', 'holder'] as const;
export type FactoryStoreAccountSubmitter = typeof FACTORY_STORE_ACCOUNT_SUBMITTERS[number];

export const FACTORY_STORE_ACCOUNT_CEREMONIES = ['operator', 'reseller', 'customer'] as const;

/** Stores the App Center onboards today (Apple comes with its shell). */
export const FACTORY_STORES = ['roku', 'amazon', 'google'] as const;
export type FactoryStoreKey = typeof FACTORY_STORES[number];

export interface FactoryStoreInfo {
  key: FactoryStoreKey;
  label: string;
  /** The store's developer portal. */
  portalUrl: string;
  /** Where in the portal the user is added (one sentence, verified in the demo walk). */
  inviteLocation: string;
  /** The download button on a submission card: what the store's binary is. */
  packageLabel: string;
  /**
   * The build system itself records "Ready to submit" the moment a build passes
   * its checks (Amazon, Google Play). With a delegated account that state is
   * never a task for the customer: the operator submits the update.
   */
  readyIsAutomatic: boolean;
  /**
   * What the operator adds to the account once the invite is accepted, when the
   * holder must leave it in place (`{operator}` = the operator's name). Shown
   * under the store's invite steps.
   */
  afterInvite?: string;
}

export const FACTORY_STORE_INFO: Record<FactoryStoreKey, FactoryStoreInfo> = {
  roku: {
    key: 'roku',
    label: 'Roku',
    portalUrl: 'https://developer.roku.com',
    inviteLocation: 'Manage My Account → Users → Add User',
    packageLabel: 'Download package (.pkg)',
    readyIsAutomatic: false,
  },
  amazon: {
    key: 'amazon',
    label: 'Amazon Appstore',
    portalUrl: 'https://developer.amazon.com',
    inviteLocation: 'My Settings → User Permissions → Add User',
    packageLabel: 'Download app file (.apk)',
    readyIsAutomatic: true,
  },
  google: {
    key: 'google',
    label: 'Google Play',
    portalUrl: 'https://play.google.com/console',
    inviteLocation: 'Users and permissions → Invite new users',
    packageLabel: 'Download app bundle (.aab)',
    readyIsAutomatic: true,
    afterInvite:
      'Once the invite is accepted, {operator} adds its publishing service to the account (a user ending in iam.gserviceaccount.com). Leave it in place: it is how updates reach Google Play without you signing in.',
  },
};

/**
 * A store whose FIRST version cannot be submitted for the customer by the build
 * system (Amazon and Google Play: their publishing services only update an app
 * that already exists in the console). Shown on the platform's Status tab until
 * the store's app id is recorded — or, for a store that knows the app by its
 * package name, until a submission has gone past Ready to submit; stores
 * without an entry have no such step.
 */
export interface FactoryStoreFirstSubmission {
  title: string;
  facts: string[];
  /** Who does it, per the account's submitter; `{operator}` = the operator's name. */
  byOperator: string;
  byHolder: string;
  noAccount: string;
  after: string;
}

export const FACTORY_STORE_FIRST_SUBMISSION: Partial<Record<FactoryStoreKey, FactoryStoreFirstSubmission>> = {
  amazon: {
    title: 'The first Amazon submission is done in the Amazon developer console',
    facts: [
      'Amazon only accepts automatic submissions for an app that already exists in its developer console, so the first version is entered there by hand.',
      'That first version sets what the console keeps for every later one: availability and pricing, the content rating, the store listing and images, and the first app file.',
    ],
    byOperator:
      '{operator} enters the first version for you through your Amazon developer account once the first build passes its checks — nothing to do.',
    byHolder:
      'You create the app in the Amazon developer console, paste its Amazon App ID on the Settings tab, and enter the first version with the app file from the submission card below — then record each step here.',
    noAccount:
      'Set up the Amazon developer account first — the Readiness card below opens that step.',
    after:
      'Once the Amazon App ID is recorded and Amazon approves the first version, every later update is submitted without the console.',
  },
  // Google Play knows the app by its package name: no store app id to record,
  // so the card stays until a submission has left Ready to submit.
  google: {
    title: 'The first Google Play release is done in the Play Console',
    facts: [
      'Google Play publishes automatic updates only for an app that already exists in the Play Console, so the first version is created there by hand.',
      'That first version sets what the console keeps for every later one: the Android TV form factor, the Data safety and content-rating declarations, the target audience, the privacy policy, the store listing and images, and the first app bundle.',
    ],
    byOperator:
      '{operator} creates the app in the Play Console through your Google Play developer account and publishes the first version once the first build passes its checks — nothing to do.',
    byHolder:
      'You create the app in the Play Console, complete the Android TV form factor, the declarations and the store listing, and publish the first version with the app bundle from the submission card below — then record each step here.',
    noAccount:
      'Set up the Google Play developer account first — the Readiness card below opens that step.',
    after:
      'Once Google Play publishes the first version, every later update is published without the console.',
  },
};

/**
 * The build request's refusals, in the customer's words (holder-requested
 * builds, 3.34.0). Keys are the factory's 422 tokens: the admission reasons
 * (`problems[].field`) and the top-level codes. `{platform}` = the platform's
 * label, `{supportContact}` = the brand's support contact; an unknown token
 * falls back to the factory's own sentence.
 */
export const FACTORY_BUILD_REFUSALS: Record<string, string> = {
  // admission reasons (problems[].field)
  missing_images: "Add your app's images first — open Brand Kit, generate them and approve the set.",
  no_web_domain: "Set up your app's address first (Store Setup).",
  no_content_tree: "Add categories or series to your app's content first.",
  empty_content_tree: "Add categories or series to your app's content first.",
  no_signing_key: 'The Roku signing key for this app is not set up yet — contact {supportContact}.',
  no_platform_row: 'Turn the {platform} app on in Settings first.',
  platform_inactive: 'Turn the {platform} app on in Settings first.',
  no_platform_title: 'Enter the App Name in Settings first.',
  platform_title_too_long: 'Shorten the App Name in Settings: Google Play allows at most 30 characters.',
  no_store_package_id: "The app's package name is missing or invalid — contact {supportContact}.",
  bad_store_package_id: "The app's package name is missing or invalid — contact {supportContact}.",
  release_not_for_platform: 'That app version is not available for {platform}.',
  // top-level codes
  platform_not_buildable: '{platform} apps cannot be built right now — contact {supportContact}.',
  shell_release_not_found: 'That app version is no longer available. Reload the page and try again.',
};

/** The submission edges a HOLDER (the customer or their reseller) may record; the operator records the rest. */
export const FACTORY_HOLDER_SUBMISSION_FROM_STATES = [
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
] as const;

// ── Query keys ──
export const APP_FACTORY_QUERY_KEYS = {
  all: ['appFactory'] as const,
  storeAccounts: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'storeAccounts', cId] as const,
  releases: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'releases', cId] as const,
  tasks: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'tasks', cId] as const,
  builds: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'builds', cId, appCfgId] as const,
  submissions: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'submissions', cId, appCfgId] as const,
  devices: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'devices', cId] as const,
  activeSubmissions: (cId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'activeSubmissions', cId] as const,
  campaigns: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'campaigns', cId] as const,
  campaignTargets: (cId: number, campaignId: string) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'campaignTargets', cId, campaignId] as const,
  /** Every platform's asset-set read of one app — what a Brand Kit generate or decision invalidates. */
  assetSetsApp: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'assetSets', cId, appCfgId] as const,
  assetSets: (cId: number, appCfgId: number, platform: string) =>
    [...APP_FACTORY_QUERY_KEYS.assetSetsApp(cId, appCfgId), platform] as const,
} as const;

// ── Asset-set states + labels/colors ──
export const ASSET_SET_STATE_LABELS: Record<string, string> = {
  generated: 'Awaiting Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const ASSET_SET_STATE_COLORS: Record<string, string> = {
  generated: 'orange',
  approved: 'green',
  rejected: 'gray',
};

/** The history badge of the set whose art is live now (the newest approval). */
export const ASSET_SET_LIVE_LABEL = 'Live';

/** How the history row words a decision, per state. */
export const ASSET_SET_DECIDED_VERB: Record<string, string> = {
  approved: 'approved',
  rejected: 'discarded',
};

// ── Deliverable slots (mirrors the factory spec table, assets/specs.go) ──
//
// The manual upload arm builds ONE asset set from these slots; the factory
// re-validates mechanically (ADR-0012 gates) and approval publishes. A
// partial set is legal — it overlays onto the current set. One table per
// platform (3.34.0): the factory stages and promotes each platform's set
// under its own folder and judges it against that platform's spec rows.

/** The platforms the factory has a deliverable set for (the asset-set `platform` field). */
export const FACTORY_ASSET_PLATFORMS = ['roku', 'firetv'] as const;
export type FactoryAssetPlatform = typeof FACTORY_ASSET_PLATFORMS[number];

export interface AssetDeliverableSlot {
  /** Exact spec basename — the manual-mode files key. */
  name: string;
  label: string;
  w: number;
  h: number;
  /** Required encoding, enforced server-side ('png' needs alpha for oh_logo). */
  format: 'png' | 'jpg';
  group: string;
  /**
   * How w x h binds: 'exact' (absent) = the file must be exactly that size;
   * 'max' = a bounded mark — at most that size on each side (the factory
   * also requires it trimmed to its visible edges).
   */
  sizeRule?: 'exact' | 'max';
  /**
   * Other exact sizes the slot still accepts (an 'exact' slot only): the TV
   * banner is 640x360 since TV graphics v2 and 320x180 files stay valid.
   */
  altSizes?: { w: number; h: number }[];
  /** One line under the picker: what the file is, in the customer's words. */
  hint?: string;
  /**
   * The one TV platform that shows this file (the Android-family set serves
   * Fire TV and Google TV): while that platform is off for the app, the slot
   * and the file are left out of what the customer sees (3.42.0).
   */
  tvOnly?: 'firetv' | 'googletv';
}

export const ASSET_DELIVERABLE_GROUPS = {
  PACKAGE: 'Channel package',
  EXPERIENCE: 'In-app experience',
  APP: 'App',
  LISTING: 'Store listing',
} as const;

export const FACTORY_DELIVERABLE_SLOTS: Record<FactoryAssetPlatform, AssetDeliverableSlot[]> = {
  roku: [
    { name: 'icon_fhd.png', label: 'Channel Icon (FHD)', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'icon_hd.png', label: 'Channel Icon (HD)', w: 290, h: 218, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'icon_sd.png', label: 'Channel Icon (SD)', w: 246, h: 140, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_fhd.jpg', label: 'Splash Screen (FHD)', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_hd.jpg', label: 'Splash Screen (HD)', w: 1280, h: 720, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_sd.jpg', label: 'Splash Screen (SD)', w: 720, h: 480, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    // The earlier (1.1) Roku channel's two backgrounds (bg_fhd.jpg,
    // oh_bg_fhd.jpg) are no deliverable since 3.46.0 (appfactory ADR-0052): a
    // background slot on a platform tab implied a per-platform background the
    // product does not have. The one Home image is set in the Brand Kit.
    { name: 'oh_logo_fhd.png', label: 'Overhang Logo (transparent)', w: 400, h: 400, format: 'png', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
    { name: 'store_poster.png', label: 'Store Poster', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
    { name: 'screenshot_1.png', label: 'Store Screenshot 1', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
    { name: 'screenshot_2.png', label: 'Store Screenshot 2', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
  ],
  // The Android-family set (Fire TV and Google TV build from it — the
  // googletv platform's assetPlatform is firetv; TV graphics v2, design
  // jvm notes/ott/tv-graphics). Each hint names the SCREEN that shows the
  // file: Google TV's Your apps row shows the circle icon (the two layers),
  // its Apps page the banner; a Fire TV home row shows the store listing's
  // 1280x720 tile (never the app's own banner); the stores' listing art is
  // uploaded by whoever publishes the store account, from Current Images.
  firetv: [
    // The first five are INSIDE every Android build, Fire TV's too: the banner is
    // a build prerequisite and the two icon layers become the app's launcher
    // icon whenever they are in the set — so they are never hidden by platform
    // (a customer who replaces the App Icon by hand must be able to replace its
    // layers), and their wording names no store or platform.
    { name: 'icon.png', label: 'App Icon', w: 512, h: 512, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The store icon and the app icon in the TV settings.' },
    { name: 'icon-foreground.png', label: 'App Icon (symbol layer)', w: 432, h: 432, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The front layer of the round app icon: your symbol on a transparent 432 x 432 PNG, centred. Upload it together with the background layer.' },
    { name: 'icon-background.png', label: 'App Icon (background layer)', w: 432, h: 432, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The colour behind the symbol in the round app icon: an opaque 432 x 432 PNG.' },
    { name: 'tv-banner.png', label: 'TV Banner', w: 640, h: 360, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, altSizes: [{ w: 320, h: 180 }], hint: 'The wide icon a TV shows in its list of installed apps (640 x 360; 320 x 180 still accepted).' },
    {
      name: 'splash-logo.png',
      label: 'Splash Logo',
      w: 1152,
      h: 1152,
      format: 'png',
      group: ASSET_DELIVERABLE_GROUPS.APP,
      sizeRule: 'max',
      hint: 'A transparent PNG of your logo mark, trimmed to its edges, up to 1152 x 1152.',
    },
    { name: 'listing-icon-small.png', label: 'Store Icon (small)', w: 114, h: 114, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The small icon of the Amazon listing.', tvOnly: 'firetv' },
    { name: 'listing-icon.png', label: 'Store Icon', w: 1280, h: 720, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The wide home-screen icon from your store listing (1280 x 720).' },
    { name: 'listing-background.jpg', label: 'Store Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The background of the Fire TV app page in the Amazon listing.', tvOnly: 'firetv' },
    { name: 'listing-feature-graphic.png', label: 'Feature Graphic', w: 1024, h: 500, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The wide image at the top of the Google Play listing.', tvOnly: 'googletv' },
  ],
};

/** Per-file cap for manual deliverable uploads. */
export const ASSET_DELIVERABLE_MAX_BYTES = 10 * 1024 * 1024;

// ── TV graphics v2 (design jvm notes/ott/tv-graphics/design_tv_graphics_20260928.md) ──

/**
 * The factory's quality warning on a Brand Kit's background image whose mean
 * brightness is above 50 % (design ADR-0016 D5 / §Dashboard): the Home shows
 * the image as uploaded, so a bright one weakens the text over it. Keyed on
 * the CODE wherever the quality report carries it.
 */
export const HOME_IMAGE_BRIGHT_CODE = 'home_image_bright';
/** The same threshold, for the advice the form gives at pick time (mean luminance 0–1). */
export const HOME_IMAGE_BRIGHT_MEAN = 0.5;

/**
 * The factory's quality warning codes (C1) in the customer's words, each
 * with the fix. An unknown code (a newer factory) renders neutrally.
 */
export const TV_QUALITY_WARNING_COPY: Record<string, string> = {
  wide_logo_in_circle:
    'Your logo is wide. In a round or square icon it looks tiny — add a square symbol.',
  low_contrast: 'Your logo and background colour are too close — pick a stronger background colour.',
  small_fill: 'Your logo fills little of the image — use a tighter crop or a symbol.',
  text_fallback:
    'We set your app name in type because there is no wordmark — upload one to use your own lettering.',
  /** The background image's mean brightness is above 50 % (design ADR-0016 §Dashboard, verbatim). */
  [HOME_IMAGE_BRIGHT_CODE]:
    'This image is bright; the text over it may be hard to read. A darker image or a darker left side reads better.',
};

/** The Brand kit's TV wording (the Android-family set). */
export const TV_BRAND_KIT_COPY = {
  symbolLabel: 'Symbol',
  symbolWhy: 'Google TV shows your app as a circle — a square mark fills it.',
  symbolWhyFireTv: "Your symbol fills the store icon and leads your app's icon on the Fire TV home screen.",
  symbolHelp: 'A square transparent PNG of your logo mark, at least 1024 x 1024.',
  wordmarkLabel: 'Wordmark (optional)',
  wordmarkHelp: 'Your wide logo or lettering, transparent PNG — it goes beside or under the symbol on the wide icons.',
  appNameLabel: 'App name on your icon',
  appNameHelp: "Shown in type on your app's wide icon when there is no wordmark.",
  groundLabel: 'Background colour',
  groundHelp: 'The colour behind your logo on every image. A strong brand colour stands out on a TV home screen.',
  suggestedGround: 'Use the colour of your symbol',
  wideSymbolWarning: 'This mark is wide. A square symbol fills the app icon much better.',
  noTransparency: 'This image has no transparent pixels. A transparent PNG sits cleanly on the background colour.',
  tooSmallSymbol: 'This symbol is smaller than 1024 x 1024 and may look soft on a 4K TV.',
  v1Notice:
    'Your TV app icons can look better. Open Brand Kit and generate them again with a square symbol — nothing changes until you approve the new set.',
} as const;

/**
 * The Roku tab's Brand kit wording where it differs from the Android family
 * (jvm notes/ott/tv-graphics/design_roku_parity_20260928.md R6). Roku's
 * poster sits on the ground colour, never on the background image.
 */
export const ROKU_BRAND_KIT_COPY = {
  symbolWhy: 'Your symbol and wordmark fill the Roku home-screen poster together.',
} as const;

/**
 * Roku preview geometry in pixels of a 1920 x 1080 screen (drawn 1:1 since
 * 3.42.0) — approximate: Roku publishes the 540 x 405 poster art, not the
 * home-screen tile's on-screen size.
 */
export const ROKU_PREVIEW_GEOMETRY = {
  frameW: 1920,
  frameH: 1080,
  tilePx: { w: 380, h: 285 },
  gapPx: 24,
} as const;

/** Upper bound of the app name set in type on the tiles. */
export const TV_TILE_TEXT_MAX = 40;
/** A symbol whose longer side exceeds this multiple of the shorter one reads small in a circle. */
export const TV_SYMBOL_MAX_ASPECT = 1.6;
/** The smallest symbol side that stays crisp on a 4K TV. */
export const TV_SYMBOL_MIN_PX = 1024;

/**
 * Preview geometry in pixels of a 1920 x 1080 screen (the previews draw these
 * 1:1 since 3.42.0 — the true on-screen size). The Google TV circle: the
 * launcher icon is 80 dp (160 px at 1080p, xhdpi) and shows the central
 * 72 dp of the 108 dp adaptive layers. The Apps-page banner: 160 x 90 dp.
 * The Fire TV home tile: approximate (Amazon publishes the 1280 x 720 art
 * and its 882 x 448 content-safe area, not the tile's on-screen size).
 */
export const TV_PREVIEW_GEOMETRY = {
  frameW: 1920,
  frameH: 1080,
  googleTvCirclePx: 160,
  adaptiveVisibleFrac: 72 / 108,
  googleTvBannerPx: { w: 320, h: 180 },
  fireTvTilePx: { w: 336, h: 189 },
  fireTvSafeArea: { w: 882, h: 448, of: { w: 1280, h: 720 } },
} as const;


// ── App-level Brand Kit (3.42.0; jvm notes/ott/tv-graphics/design_brand_kit_app_level_20260930.md) ──
//
// ONE kit per app — symbol, wordmark, app name, colours, tile art — stored by
// the factory; every TV platform's image set is a render of it. The form lives
// once, on the app's Brand Kit tab; each platform tab's Images keeps its
// current images, its TV screens and the per-file uploads.

/** The TV platforms an app can have on, as the copy and the previews name them. */
export const TV_PLATFORM_KEYS = ['roku', 'firetv', 'googletv'] as const;
export type TvPlatformKey = typeof TV_PLATFORM_KEYS[number];

export const TV_PLATFORM_LABELS: Record<TvPlatformKey, string> = {
  roku: 'Roku',
  firetv: 'Fire TV',
  googletv: 'Google TV',
};

/** Which TV platforms each factory image set serves (Fire TV and Google TV share one). */
export const FACTORY_ASSET_PLATFORM_TV: Record<FactoryAssetPlatform, TvPlatformKey[]> = {
  roku: ['roku'],
  firetv: ['firetv', 'googletv'],
};

/** A fill at or above this reads as "Good"; between the floor and this, "OK". */
export const TV_QUALITY_GOOD_FILL_PCT = 60;

export const TV_QUALITY_STATUSES = ['good', 'ok', 'small', 'hard'] as const;
export type TvQualityStatus = typeof TV_QUALITY_STATUSES[number];

export const TV_QUALITY_STATUS_LABELS: Record<TvQualityStatus, string> = {
  good: 'Good',
  ok: 'OK',
  small: 'Too small',
  hard: 'Hard to see',
};

export const TV_QUALITY_STATUS_COLORS: Record<TvQualityStatus, string> = {
  good: 'green',
  ok: 'gray',
  small: 'red',
  hard: 'red',
};

/** The factory's codes that block an approval. */
export const TV_QUALITY_BLOCK_CODES = {
  SMALL_FILL: 'small_fill',
  LOW_CONTRAST: 'low_contrast',
} as const;

/**
 * One row of the quality table: the SCREEN a file is shown on, in the
 * customer's words. Files that share a `key` collapse into one row showing the
 * weakest number. `labels` is tried in order — the first whose platform is on
 * for the app names the row ('any' = always); a row none of whose platforms is
 * on is hidden, unless it blocks the approval, when `fallback` names it
 * without naming a platform the customer does not have.
 */
export interface TvQualityRowInfo {
  key: string;
  labels: { platform: TvPlatformKey | 'any'; label: string }[];
  fallback: string;
}

const ROKU_POSTER_ROW: TvQualityRowInfo = {
  key: 'roku-poster',
  labels: [{ platform: 'roku', label: 'Roku home screen' }],
  fallback: 'Channel poster',
};
const SPLASH_ROW: TvQualityRowInfo = {
  key: 'splash',
  labels: [{ platform: 'any', label: 'Splash screen' }],
  fallback: 'Splash screen',
};
const STORE_ICON_ROW: TvQualityRowInfo = {
  key: 'store-icon',
  labels: [{ platform: 'any', label: 'Store icon' }],
  fallback: 'Store icon',
};

export const TV_QUALITY_ROWS: Record<string, TvQualityRowInfo> = {
  'icon-foreground.png': {
    key: 'google-tv-circle',
    labels: [{ platform: 'googletv', label: 'Google TV home screen' }],
    fallback: 'Round app icon',
  },
  'tv-banner.png': {
    key: 'google-tv-banner',
    labels: [{ platform: 'googletv', label: 'Google TV Apps page' }],
    fallback: 'App banner',
  },
  'listing-icon.png': {
    key: 'wide-tile',
    labels: [
      { platform: 'firetv', label: 'Fire TV home screen' },
      { platform: 'googletv', label: 'Google Play TV banner' },
    ],
    fallback: 'Wide app icon',
  },
  'icon.png': STORE_ICON_ROW,
  'listing-icon-small.png': STORE_ICON_ROW,
  'listing-feature-graphic.png': {
    key: 'play-feature',
    labels: [{ platform: 'googletv', label: 'Google Play listing' }],
    fallback: 'Feature graphic',
  },
  'splash-logo.png': SPLASH_ROW,
  'icon_fhd.png': ROKU_POSTER_ROW,
  'icon_hd.png': ROKU_POSTER_ROW,
  'icon_sd.png': ROKU_POSTER_ROW,
  'store_poster.png': {
    key: 'roku-store',
    labels: [{ platform: 'roku', label: 'Roku Channel Store' }],
    fallback: 'Store poster',
  },
  'splash_fhd.jpg': SPLASH_ROW,
  'splash_hd.jpg': SPLASH_ROW,
  'splash_sd.jpg': SPLASH_ROW,
};

/** The quality surfaces that are an icon (round or square): a wide mark cannot fill them. */
export const TV_QUALITY_ICON_SURFACES = ['circle', 'icon'] as const;

/** Why a draft is blocked, with the fix — one sentence per cause, the screens it concerns listed after it. */
export const TV_QUALITY_BLOCK_FIXES = {
  symbol: 'Your logo is too wide for a round or square icon — it would look tiny. Add a square symbol.',
  longName: 'Your app name is long for the wide icons. Shorten the app name on your icon, or upload a wordmark.',
  stack: 'Your logo is a thin strip on the wide icons. Add a square symbol — it sits with your wordmark and fills the icon.',
  contrast: 'Your logo is hard to see on this background colour. Pick a background colour that contrasts with it.',
  unknown: 'Something about this image needs another look',
} as const;

export const BRAND_KIT_REASON_MAX = 500;

/** The Brand Kit tab's wording; `{platforms}` = the TV apps that are on, `{platform}` = one of them. */
export const BRAND_KIT_COPY = {
  tab: 'Brand Kit',
  intro:
    'Your brand, entered once. It makes the home-screen images, the splash and the store art for {platforms}, and you see each one at the size a TV shows it before anything goes live.',
  noPlatformTitle: 'Turn on a TV app first',
  noPlatform:
    'The Brand Kit makes the images of your TV apps. Turn on a TV app on its tab, then come back here.',
  unavailable:
    'The Brand Kit is temporarily unavailable. Your apps and their current images are unaffected — try again in a few minutes, or contact support if this persists.',
  generateSlowTitle: 'Still working',
  generateSlowMsg:
    'Making the images is taking longer than usual. The drafts appear here in a moment — no need to generate again.',
  formTitle: 'Your brand',
  formTitleDrafts: 'Change your brand',
  storedSource: 'In your Brand Kit',
  seededSource: 'From your Roku images',
  seededNotice: 'We started your Brand Kit from your Roku logo.',
  seededSymbolNudge: 'Add a square symbol to fill the Google TV circle.',
  seededSymbolNudgeIcons: 'Add a square symbol to fill the store icon.',
  symbolNeeded: 'Choose a symbol or a wordmark to begin.',
  groundNeeded: 'Pick a background colour, for example #10213F.',
  platformsLabel: 'Make images for',
  platformNone: 'no images yet',
  platformGenerated: 'replaces its current Brand Kit images',
  platformCustom: 'keeps its current images — tick to replace them with Brand Kit images',
  platformsNeeded: 'Tick at least one TV app.',
  generate: 'Generate images',
  generateAgain: 'Generate again',
  generateNote: 'You see every image on a TV screen before anything goes live.',
  generateAgainNote: 'The new drafts replace the ones waiting above.',
  generatedTitle: 'Images generated',
  generatedMsg: 'Review each screen, then approve to publish.',
  quickTitle: '{platform}: no images yet',
  quickBody: 'Your Brand Kit can make them — there is nothing to upload.',
  quickAction: 'Generate {platform} images',
  draftsTitle: 'Review your new images',
  draftsIntro:
    'Each screen below is drawn at the size a 1080p TV shows it. Nothing changes in your apps until you approve.',
  approveAll: 'Approve all & publish',
  approveAllTitle: 'Publish these images?',
  approveAllBody:
    'Approving replaces the images of {platforms}. The new images take effect in your next build of each app.',
  approveAllBlocked: '{count} of these drafts cannot be approved yet and stay as drafts.',
  approveOne: 'Approve & publish',
  discardOne: 'Discard',
  discardAll: 'Discard all',
  discardAllTitle: 'Discard these drafts?',
  discardAllBody: 'The drafts are thrown away. Your live images and your Brand Kit stay as they are.',
  decisionSlowTitle: 'Still working',
  decisionSlowMsg: 'This is taking longer than usual. The page shows the result in a moment — no need to do it again.',
  blockedTitle: 'This draft cannot be approved yet',
  blockedTooltip: 'Fix what is listed above, then generate again.',
  overrideAction: 'Approve anyway…',
  overrideTitle: 'Approve a blocked draft',
  overrideBody:
    'This draft is under the quality floor. Approving it publishes it as it is. The reason is kept with the set.',
  overrideReasonLabel: 'Reason',
  overrideConfirm: 'Approve & publish',
  overrideNotAllowed:
    'A blocked draft can only be approved by an operator, and this approval was not accepted as one. Your reason is kept in the box; an operator can also approve it from the ops shell.',
  publishedTitle: 'Images published',
  publishedMsg: 'The approved images take effect in your next build.',
  publishedPartTitle: 'Some drafts still need a fix',
  publishedPartMsg: 'Published {done}. {blocked} stayed a draft — see what to fix on it.',
  discardedTitle: 'Drafts discarded',
  discardedMsg: 'Generate again whenever you are ready.',
  qualityTitle: 'How large and clear it is on each screen',
  qualityFill: 'Fills',
  qualityFloor: 'needs at least {floor}%',
  overrideBadge: 'Approved by an operator',
  /** Under "Current Images"; `{platforms}` = the TV apps of this image set that are on. */
  currentIntro:
    'The images in use today for {platforms}, in the app and on its store listing. They come from your Brand Kit, or from images you upload below.',
  pointerTitle: 'Brand Kit',
  pointerEmpty:
    'Make these images from your Brand Kit: enter your brand once and it creates the images for every TV app you have on.',
  pointerLive:
    'To change your logo, colours or app name on these images, open Brand Kit. It updates every TV app together.',
  pointerDraft: 'New images from your Brand Kit are waiting for your review.',
  pointerOpen: 'Open Brand Kit',
  pointerReview: 'Review in Brand Kit',
  /** Web > Images (3.46.0): the Home image is not a web upload — it is the app's, set in the Brand Kit. */
  webPointerTitle: 'Home screen background',
  webPointer: "The picture behind your Web App's home screen is set in your Brand Kit, together with your TV apps.",
  manualDraftTitle: 'Review your draft',
} as const;

/**
 * The home background switch on the Brand Kit tab (3.44.0; design ADR-0016 D5
 * and §Dashboard — the label and its help are the record's words; one word
 * changed: the record's "tiles" is "rows", by the owner's plain-words rule).
 * An app setting, saved at once; off by default. The image shows AS UPLOADED:
 * no darkening, no blur.
 */
export const HOME_BACKGROUND_COPY = {
  title: 'Home screen background',
  /**
   * The card's one line (3.46.0; appfactory ADR-0052): the image is a Home
   * setting, saved on its own; the switch shows it on every app of the
   * account — the Web App and the TV apps alike (design ADR-0016 D5).
   */
  intro: 'One picture behind the home screen of your apps. Saved at once; the switch shows it.',
  /** The Brand Kit tab of an app with no TV app on: the card alone. */
  introNoTv: "Your background image, shown on your Web App's home screen. Turn on a TV app on its tab to make its images here too.",
  imageLabel: 'Background image',
  imageChoose: 'Choose Background image',
  imageReplace: 'Replace Background image',
  imageRemove: 'Remove',
  imageKeep: 'Keep the current one',
  imageSave: 'Save Background image',
  imageSaveRemoval: 'Remove Background image',
  imageStored: 'In your Brand Kit',
  /** The size, then where the image is used; the variants name only the TV apps that draw it behind their icon. */
  imageHelp: '16:9, 1920 x 1080 recommended. Shown on the home screen of your apps with the switch below.',
  imageHelpFireTv: "16:9, 1920 x 1080 recommended. Shown on the home screen of your apps with the switch below; also used behind your app's icon on the Fire TV home screen the next time you generate images.",
  imageHelpGoogleTv: "16:9, 1920 x 1080 recommended. Shown on the home screen of your apps with the switch below; also used behind your app's icon on the Google TV Apps page the next time you generate images.",
  imageHelpBoth: "16:9, 1920 x 1080 recommended. Shown on the home screen of your apps with the switch below; also used behind your app's icon on the Fire TV home screen and on the Google TV Apps page the next time you generate images.",
  /** Advice at pick time, the same words as the factory's warning (TV_QUALITY_WARNING_COPY). */
  bright: 'This image is bright; the text over it may be hard to read. A darker image or a darker left side reads better.',
  switchLabel: 'Show my background image on the home screen',
  switchHelp:
    "Your image fills the home screen of your apps exactly as uploaded; the featured title's text sits on its left side over a dark gradient, and your videos appear in rows below. While it is on, the hero shows the title and button over your picture and the Hero Style on the General tab is not used. Best for an app with one live stream or a small number of videos. Search and About keep the plain dark background. The preview shows the result.",
  /** The General tab's Hero Style select while the background image is on (design ADR-0016 D5: the hero draws text only over the picture). */
  heroStyleNote: 'Not used while your background image is on. The hero shows the title and button over your picture.',
  noteNone: 'Add a background image first.',
  noteLight: 'Available with the dark theme.',
  previewCaption: 'Preview of your home screen with the background image.',
  imageCaption: 'Your background image as your apps show it on the home screen.',
  imageSavedOn: 'Background image saved. Your apps show it the next time they open, within a few minutes.',
  imageSavedOff: 'Background image saved. Turn the switch on to show it.',
  imageRemoved: 'Background image removed. Your apps show the plain background the next time they open, within a few minutes.',
  savedOn: 'The background image is on. Your apps show it the next time they open, within a few minutes.',
  savedOff: 'The background image is off. Your apps show the plain background the next time they open, within a few minutes.',
  notAvailable: 'This setting is not available yet. Try again later.',
  conflict: 'This app was changed in another window. Refresh the page and try again.',
} as const;

/** The true on-screen strips: what the caption under a TV-size preview says. */
export const TV_TRUE_SIZE_NOTE = 'Shown at the size a 1080p TV draws it.';

/** A refused Brand Kit generate, in the customer's words: the factory's codes and the fields it names. */
export const BRAND_KIT_GENERATE_ERRORS: Record<string, string> = {
  no_tv_platform: BRAND_KIT_COPY.noPlatform,
  brand_kit_missing: 'Your Brand Kit has nothing saved yet. Fill in the form, then generate.',
  render_failed: 'These images could not be made from your files. Check that each one is a PNG or JPG that opens, then try again.',
  asset_validation_failed: 'The images made from these inputs did not pass the store checks, so nothing was saved. Try a different symbol or wordmark.',
  symbolBase64: 'Choose a symbol or a wordmark first.',
  'symbolBase64:unreadable': 'Your symbol could not be read. Use a PNG that opens in an image viewer, at most 8192 x 8192.',
  'wordmarkBase64:unreadable': 'Your wordmark could not be read. Use a PNG that opens in an image viewer, at most 8192 x 8192.',
  'tileArtBase64:unreadable': 'Your background image could not be read. Use a PNG or JPG that opens in an image viewer.',
  bgColorHex: BRAND_KIT_COPY.groundNeeded,
  platforms: 'One of the TV apps you ticked is not turned on. Reload the page and try again.',
};

/** A refused decision, in the customer's words. */
export const BRAND_KIT_DECISION_ERRORS: Record<string, string> = {
  quality_gate_blocked: 'This draft cannot be approved yet — fix what is listed on it, then generate again.',
  override_not_allowed: BRAND_KIT_COPY.overrideNotAllowed,
  already_decided: 'This draft was already approved or discarded. The page now shows where it stands.',
};
